import type {
  DisplayNamesRequest,
  DisplayNamesResponse,
  EntityRecordRequest,
  FieldValueParams,
  FieldValueResponse,
  EntityRecordDetail,
  SearchEntityRecordsResponse,
  SearchEntityRecordsOptions,
} from '@kizen/api/records';
import type {
  DefaultError,
  InfiniteData,
  InfiniteQueryObserverOptions,
  QueryObserverOptions,
} from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type {
  Drop,
  WithInfiniteSelect,
  WithParams,
  WithRequired,
} from './types';

import {
  getDisplayNames,
  getEntityRecord,
  getFieldValues,
  searchEntityRecords,
} from '@kizen/api/records';
import { getResource } from './resource';
import { curry1, parsePageParam } from './utils';

export type SearchEntityRecordsInfiniteQueryOptions<
  TData = InfiniteData<SearchEntityRecordsResponse, string | number>,
> = WithParams<
  Omit<
    SearchEntityRecordsInfiniteQuery<TData>,
    | 'queryFn'
    | 'queryKey'
    | 'getNextPageParam'
    | 'getPreviousPageParam'
    | 'initialPageParam'
  >,
  SearchEntityRecordsOptions['params']
> & {
  body?: SearchEntityRecordsOptions['body'];
  scope?: string[];
};

type DisplayNamesQuery<TData = DisplayNamesResponse> = WithRequired<
  QueryObserverOptions<
    DisplayNamesResponse,
    DefaultError,
    TData,
    DisplayNamesResponse,
    [Resource<Readonly<['records', 'display-names']>, DisplayNamesRequest>]
  >,
  'queryFn' | 'queryKey'
>;

type EntityRecordQuery<TData = EntityRecordDetail> = WithRequired<
  QueryObserverOptions<
    EntityRecordDetail,
    DefaultError,
    TData,
    EntityRecordDetail,
    [Resource<Readonly<['records', string, string]>, EntityRecordRequest>]
  >,
  'queryFn' | 'queryKey'
>;

type FieldValueQuery<TData = FieldValueResponse> = WithRequired<
  QueryObserverOptions<
    FieldValueResponse,
    DefaultError,
    TData,
    FieldValueResponse,
    [
      Resource<
        Readonly<['records', string, 'field-value', string]>,
        FieldValueParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

export type SearchEntityRecordsInfiniteQuery<
  TData = InfiniteData<SearchEntityRecordsResponse, string | number>,
> = WithRequired<
  WithInfiniteSelect<
    InfiniteQueryObserverOptions<
      SearchEntityRecordsResponse,
      DefaultError,
      TData,
      SearchEntityRecordsResponse,
      [
        Resource<
          Readonly<['records', string, 'search']>,
          SearchEntityRecordsOptions['params'],
          SearchEntityRecordsOptions['body']
        >,
      ],
      string | number
    >
  >,
  'queryFn' | 'queryKey'
>;

export type SearchEntityRecordsQueryOptions<
  TData = SearchEntityRecordsResponse,
> = WithParams<
  Omit<SearchEntityRecordsQuery<TData>, 'queryFn' | 'queryKey'>,
  SearchEntityRecordsOptions['params']
> & {
  body?: SearchEntityRecordsOptions['body'];
};

type SearchEntityRecordsQuery<TData = SearchEntityRecordsResponse> =
  WithRequired<
    QueryObserverOptions<
      SearchEntityRecordsResponse,
      DefaultError,
      TData,
      SearchEntityRecordsResponse,
      [
        Resource<
          Readonly<['records', string, 'search']>,
          SearchEntityRecordsOptions['params'],
          SearchEntityRecordsOptions['body']
        >,
      ]
    >,
    'queryFn' | 'queryKey'
  >;

export const displayNamesQuery = <TData = DisplayNamesResponse>(
  axios: AxiosInstance,
  data: DisplayNamesRequest,
  options?: Omit<DisplayNamesQuery<TData>, 'queryFn' | 'queryKey'>
): DisplayNamesQuery<TData> => {
  const path = ['records', 'display-names'] as const;
  const resource = getResource(path, { params: data });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getDisplayNames(axios, qk.params!, { signal });
    },
    ...options,
  };
};

export const entityRecordQuery = <TData = EntityRecordDetail>(
  axios: AxiosInstance,
  objectId: string,
  entityId: string,
  options: WithParams<
    Omit<EntityRecordQuery<TData>, 'queryFn' | 'queryKey'>,
    EntityRecordRequest
  > = {}
): EntityRecordQuery<TData> => {
  const path = ['records', objectId, entityId] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getEntityRecord(axios, qk.path[1], qk.path[2], {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

export const fieldValuesQuery = <TData = FieldValueResponse>(
  axios: AxiosInstance,
  objectId: string,
  entityId: string,
  options: WithParams<
    Omit<FieldValueQuery<TData>, 'queryFn' | 'queryKey'>,
    FieldValueParams
  > = {}
): FieldValueQuery<TData> => {
  const path = ['records', objectId, 'field-value', entityId] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getFieldValues(axios, qk.path[1], qk.path[3], {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

export const searchEntityRecordsQuery = <TData = SearchEntityRecordsResponse>(
  axios: AxiosInstance,
  objectId: string,
  options: SearchEntityRecordsQueryOptions<TData>
): SearchEntityRecordsQuery<TData> => {
  const path = ['records', objectId, 'search'] as const;
  const { body, params, ...queryOpts } = options;
  const resource = getResource(path, {
    method: 'POST',
    body,
    params,
  });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return searchEntityRecords(axios, qk.path[1], {
        body: qk.body,
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

export const searchEntityRecordsInfiniteQuery = <
  TData = InfiniteData<SearchEntityRecordsResponse, string | number>,
>(
  axios: AxiosInstance,
  objectId: string,
  options: SearchEntityRecordsInfiniteQueryOptions<TData>
): SearchEntityRecordsInfiniteQuery<TData> => {
  const path = ['records', objectId, 'search'] as const;
  const { body, params, scope, ...queryOpts } = options;
  const resource = getResource(path, {
    infinite: true,
    method: 'POST',
    body,
    params,
    scope,
  });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
        pageParam,
      } = context;

      const page = parsePageParam(pageParam);

      return searchEntityRecords(axios, qk.path[1], {
        body: qk.body,
        params: { ...qk.params, page },
        signal,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.next) {
        return new URL(lastPage.next).searchParams.get('page');
      }
    },
    getPreviousPageParam: (previousPage) => {
      if (previousPage.previous) {
        return new URL(previousPage.previous).searchParams.get('page');
      }
    },
    ...queryOpts,
  };
};

class Queries {
  public displayNamesQuery: <TData = DisplayNamesResponse>(
    ...args: Drop<Parameters<typeof displayNamesQuery<TData>>>
  ) => ReturnType<typeof displayNamesQuery<TData>>;

  public entityRecordQuery: <TData = EntityRecordDetail>(
    ...args: Drop<Parameters<typeof entityRecordQuery<TData>>>
  ) => ReturnType<typeof entityRecordQuery<TData>>;

  public fieldValueQuery: <TData = FieldValueResponse>(
    ...args: Drop<Parameters<typeof fieldValuesQuery<TData>>>
  ) => ReturnType<typeof fieldValuesQuery<TData>>;

  public searchEntityRecordsQuery: <
    TData = InfiniteData<SearchEntityRecordsResponse, string | number>,
  >(
    ...args: Drop<Parameters<typeof searchEntityRecordsQuery<TData>>>
  ) => ReturnType<typeof searchEntityRecordsQuery<TData>>;

  public searchEntityRecordsInfiniteQuery: <
    TData = InfiniteData<SearchEntityRecordsResponse, string | number>,
  >(
    ...args: Drop<Parameters<typeof searchEntityRecordsInfiniteQuery<TData>>>
  ) => ReturnType<typeof searchEntityRecordsInfiniteQuery<TData>>;

  constructor(axios: AxiosInstance) {
    this.displayNamesQuery = curry1(displayNamesQuery)(axios);
    this.entityRecordQuery = curry1(entityRecordQuery)(axios);
    this.fieldValueQuery = curry1(fieldValuesQuery)(axios);
    this.searchEntityRecordsQuery = curry1(searchEntityRecordsQuery)(axios);
    this.searchEntityRecordsInfiniteQuery = curry1(
      searchEntityRecordsInfiniteQuery
    )(axios);
  }
}

export type RecordsQueries = Queries;

export const defRecordsQueries = (axios: AxiosInstance): RecordsQueries => {
  return new Queries(axios);
};
